import {
    DefaultCell,
    ChangePercentage,
    IndexCell,
    KeywordAnalysisTrafficSource,
    TrafficShareWithVisits,
    UrlCellWebsiteAnalysis,
    EnrichButtonCell,
    DefaultCellRightAlign,
    SearchKeywordCell,
} from "app/components/React/Table/cells";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    HeaderCellBlank,
} from "app/components/React/Table/headerCells";
import { translate } from "app/filters/i18nFilter";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { MobileV2TableKeywordPositionCall } from "app/components/React/Table/cells/MobileV2TableKeywordPositionCall";
import { CompareBarCell } from "app/components/React/Table/cells/CompareBarCell";
import styled from "styled-components";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { CellMultipleUrls } from "app/components/React/Table/cells/CellMultipleUrls";
import { BasicDurations } from "app/services/DurationService";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import {
    KEYWORD_ANALYSIS_PREFIX,
    TABLE_COLUMN_AVG_COMPETITION,
    TABLE_COLUMN_AVG_DIFFICULTY,
    TABLE_COLUMN_COMPETITION,
    TABLE_COLUMN_CPC,
    TABLE_COLUMN_DIFFICULTY,
    TABLE_COLUMN_INTENT,
    TABLE_COLUMN_MONTHLY_VOLUME,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_SERP_FEATURES,
    TABLE_COLUMN_TOP_INTENT,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_TOP_URL,
    TABLE_COLUMN_TOTAL_MONTHLY_VOLUME,
    TABLE_COLUMN_TOTAL_WINDOW_VOLUME,
    TABLE_COLUMN_WINDOW_VOLUME,
    TABLE_COLUMN_CHANGE,
    TABLE_COLUMN_DOMAIN,
    TABLE_COLUMN_KEYWORD,
    TABLE_COLUMN_KEYWORDS,
    TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_URL,
    TABLE_COLUMN_WEB_SOURCE_SHARE,
    columnNamesAndTooltipsMap,
    TABLE_COLUMN_SELECTION,
} from "app/common/keyword-research/serp-players/consts";

import { defaultParams } from "app/Shared/defaults";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { colorsPalettes } from "@similarweb/styles";
import { dropdownPlacementTypes } from "app/UtilitiesAndConstants/Constants/dropdownTypes";
import { entities } from "app/common/keyword-research/serp-players/consts";
import { EnrichButtonContainer } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportStyledComponents";
import { getColumnTexts } from "app/common/keyword-research/domains-table/columnTexts";
import type { TextMode } from "app/common/keyword-research/domains-table/tableTypes";
import type { GetTableColumnsParams } from "./types";

const AlignRight = styled.div`
    text-align: right;
`;

const DEFAULT_SORT_FIELD = "Share";
const DEFAULT_SORT_DIRECTION = "desc";

const getTableColumns = ({
    isGroup,
    sortableFields = [],
    sortedColumn = { field: DEFAULT_SORT_FIELD, sortDirection: DEFAULT_SORT_DIRECTION },
    isTotal,
    showPosition,
    selectedPageTab,
    onNavItemClick,
    visibleColumns = [],
    duration,
    country,
    entity,
    keywordAdsState,
    keywordSerpPlayersState,
}: GetTableColumnsParams) => {
    const mode: TextMode = isGroup ? "group" : "single";
    const isTotalTabSelected = selectedPageTab === EKeywordTabs[EKeywordTabs.Total];
    const showDifficulty = selectedPageTab !== EKeywordTabs[EKeywordTabs.Paid];
    const showCompetition = selectedPageTab !== EKeywordTabs[EKeywordTabs.Organic] && country !== defaultParams.country;

    const isWindowDuration = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;

    return [
        {
            field: TABLE_COLUMN_SELECTION,
            fixed: true,
            cellComponent: RowSelectionConsumer,
            headerComponent: SelectAllRowsHeaderCellConsumer,
            sortable: false,
            isResizable: false,
            width: 48,
            disableHeaderCellHover: true,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            headerComponent: HeaderCellBlank,
            disableHeaderCellHover: true,
            sortable: false,
            isResizable: false,
            width: 48,
            visible: true,
        },
        {
            width: 220,
            sortable: false,
            field: TABLE_COLUMN_KEYWORD,
            showTotalCount: true,
            displayName: getColumnTexts(TABLE_COLUMN_KEYWORD, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_KEYWORD, mode),
            isResizable: true,
            cellComponent: DefaultCell,
        },
        {
            width: 220,
            field: TABLE_COLUMN_DOMAIN,
            showTotalCount: true,
            displayName: getColumnTexts(TABLE_COLUMN_DOMAIN, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_DOMAIN, mode),
            isResizable: true,
            cellComponent: KeywordAnalysisTrafficSource,
            fixed: true,
        },

        {
            field: TABLE_COLUMN_URL,
            displayName: getColumnTexts(TABLE_COLUMN_URL, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_URL, mode),
            width: 360,
            showTotalCount: true,
            cellComponent: (props) => (
                <CellMultipleUrls
                    presentJustOnHover={true}
                    shouldSupportLongUrl={true}
                    dropDownPlacement={dropdownPlacementTypes.ON_TOP_LEFT}
                    {...props}
                />
            ),
            onNavItemClick: (props) => {
                onNavItemClick(props);
            },
            cssClass: null,
            enabledTooltip: false,
        },
        isGroup && {
            field: TABLE_COLUMN_KEYWORDS,
            cellComponent: (props) => {
                const { row } = props;
                return (
                    <EnrichButtonCell
                        {...props}
                        buttonElement={translate(`${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORDS}.button.text`)}
                        enabledTooltip={!row.Keywords?.length}
                        markOnHoverOnRow={true}
                        presentEnrichButton={true}
                        tooltipText={translate(`${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORDS}.button.tooltip`)}
                    >
                        <EnrichButtonContainer>{row.Keywords?.length}</EnrichButtonContainer>
                    </EnrichButtonCell>
                );
            },
            headerComponent: DefaultCellHeader,
            tooltip: getColumnTexts(TABLE_COLUMN_KEYWORDS, mode, entities[entity]),
            displayName: getColumnTexts(TABLE_COLUMN_KEYWORDS, "title"),
            width: 170,
        },
        {
            field: TABLE_COLUMN_SHARE,
            displayName: getColumnTexts(TABLE_COLUMN_SHARE, "title"),
            sortable: sortableFields.includes(TABLE_COLUMN_SHARE),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp="Clicks"
                    applyAbbrNumberFilter
                    thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                    thresholdForTotalFilter={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                    displayLessThanThreshold={true}
                />
            ),
            headerComponent: DefaultCellHeader,
            tooltip: getColumnTexts(TABLE_COLUMN_SHARE, mode, entities[entity]),
            width: 148,
            isResizable: false,
        },

        {
            field: TABLE_COLUMN_CHANGE,
            displayName: getColumnTexts(TABLE_COLUMN_CHANGE, "title"),
            sortable: sortableFields.includes(TABLE_COLUMN_CHANGE),
            cellComponent: ChangePercentage,
            headerComponent: DefaultCellHeaderRightAlign,
            tooltip: getColumnTexts(TABLE_COLUMN_CHANGE, mode),
            width: 100,
        },
        isTotalTabSelected && {
            field: TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
            trackingName: TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
            displayName: getColumnTexts(TABLE_COLUMN_ORGANIC_CLICKS_SHARE, "title"),
            cellComponent: (props) => {
                if (props.row.Clicks > 0) {
                    return (
                        <CompareBarCell
                            isSourceType={true}
                            {...props}
                        />
                    );
                } else {
                    return <AlignRight>{"-"}</AlignRight>;
                }
            },
            headerComponent: DefaultCellHeader,
            tooltip: getColumnTexts(TABLE_COLUMN_ORGANIC_CLICKS_SHARE, mode),
            width: 120,
        },

        {
            field: TABLE_COLUMN_TOP_KEYWORD,
            width: 170,
            sortable: false,
            displayName: getColumnTexts(TABLE_COLUMN_TOP_KEYWORD, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_TOP_KEYWORD, mode),
            isResizable: true,
            cellComponent: (props) => {
                const navigatorParams = { ...proNavigator.getParams() };
                const adsUrl = keywordAdsState
                    ? proNavigator.href(keywordAdsState, {
                          ...navigatorParams,
                          keyword: props.value,
                      })
                    : undefined;
                return (
                    <SearchKeywordCell
                        {...props}
                        destinationState={keywordSerpPlayersState}
                        adsUrl={adsUrl}
                        navigatorParams={navigatorParams}
                    />
                );
            },
        },
        ...(isWindowDuration
            ? [
                  {
                      field: TABLE_COLUMN_WINDOW_VOLUME,
                      displayName: getColumnTexts(TABLE_COLUMN_WINDOW_VOLUME, "title"),
                      sortable: sortableFields.includes(TABLE_COLUMN_WINDOW_VOLUME),
                      tooltip: getColumnTexts(TABLE_COLUMN_WINDOW_VOLUME, mode),
                      cellComponent: DefaultCellRightAlign,
                      format: "minAbbrNumber:50",
                      headerComponent: DefaultCellHeaderRightAlign,
                      width: 166,
                  },
                  {
                      field: TABLE_COLUMN_TOTAL_WINDOW_VOLUME,
                      sortable: true,
                      displayName: getColumnTexts(TABLE_COLUMN_TOTAL_WINDOW_VOLUME, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_TOTAL_WINDOW_VOLUME, mode),
                      cellComponent: DefaultCellRightAlign,
                      format: "minAbbrNumber:50",
                      headerComponent: DefaultCellHeaderRightAlign,
                      width: 166,
                  },
              ]
            : [
                  {
                      field: TABLE_COLUMN_MONTHLY_VOLUME,
                      displayName: getColumnTexts(TABLE_COLUMN_MONTHLY_VOLUME, "title"),
                      sortable: sortableFields.includes(TABLE_COLUMN_MONTHLY_VOLUME),
                      tooltip: getColumnTexts(TABLE_COLUMN_MONTHLY_VOLUME, mode),
                      cellComponent: DefaultCellRightAlign,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minAbbrNumber:50",
                      width: 90,
                  },
                  {
                      field: TABLE_COLUMN_TOTAL_MONTHLY_VOLUME,
                      sortable: true,
                      displayName: getColumnTexts(TABLE_COLUMN_TOTAL_MONTHLY_VOLUME, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_TOTAL_MONTHLY_VOLUME, mode),
                      cellComponent: DefaultCellRightAlign,
                      headerComponent: DefaultCellHeaderRightAlign,
                      format: "minAbbrNumber:50",
                      width: 90,
                  },
              ]),
        isTotal && {
            field: TABLE_COLUMN_WEB_SOURCE_SHARE,
            displayName: getColumnTexts(TABLE_COLUMN_WEB_SOURCE_SHARE, "title"),
            cellComponent: (props) => {
                if (props.value > 0) {
                    return <CompareBarCell {...props} />;
                } else {
                    return <AlignRight>{"-"}</AlignRight>;
                }
            },
            headerComponent: DefaultCellHeader,
            tooltip: getColumnTexts(TABLE_COLUMN_WEB_SOURCE_SHARE, mode),
            width: 120,
        },
        ...(showDifficulty
            ? [
                  {
                      field: TABLE_COLUMN_DIFFICULTY,
                      displayName: getColumnTexts(TABLE_COLUMN_DIFFICULTY, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_DIFFICULTY, mode),
                      width: 68,
                      cellComponent: KeywordDifficultyCell,
                      sortable: false,
                      cellClass: "kw-difficulty-table-cell",
                  },
                  {
                      field: TABLE_COLUMN_AVG_DIFFICULTY,
                      displayName: getColumnTexts(TABLE_COLUMN_AVG_DIFFICULTY, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_AVG_DIFFICULTY, mode),
                      width: 80,
                      cellComponent: KeywordDifficultyCell,
                      sortable: false,
                      cellClass: "avg-kw-difficulty-table-cell",
                  },
              ]
            : []),
        ...(showCompetition
            ? [
                  {
                      field: TABLE_COLUMN_AVG_COMPETITION,
                      displayName: getColumnTexts(TABLE_COLUMN_AVG_COMPETITION, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_AVG_COMPETITION, mode),
                      width: 116,
                      cellComponent: KeywordDifficultyCell,
                      sortable: false,
                      cellClass: "avg-kw-competition-table-cell",
                  },
                  {
                      field: TABLE_COLUMN_COMPETITION,
                      displayName: getColumnTexts(TABLE_COLUMN_COMPETITION, "title"),
                      tooltip: getColumnTexts(TABLE_COLUMN_COMPETITION, mode),
                      width: 90,
                      cellComponent: KeywordDifficultyCell,
                      sortable: false,
                      cellClass: "kw-competition-table-cell",
                  },
              ]
            : []),
        {
            field: TABLE_COLUMN_TOP_INTENT,
            displayName: getColumnTexts(TABLE_COLUMN_TOP_INTENT, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_TOP_INTENT, mode),
            width: 130,
            cellComponent: IntentCell,
        },
        {
            field: TABLE_COLUMN_INTENT,
            displayName: getColumnTexts(TABLE_COLUMN_INTENT, "title"),
            tooltip: getColumnTexts(TABLE_COLUMN_INTENT, mode),
            width: 130,
            cellComponent: IntentCell,
        },
        {
            field: TABLE_COLUMN_CPC,
            displayName: getColumnTexts(TABLE_COLUMN_CPC, "title"),
            sortable: sortableFields.includes(TABLE_COLUMN_CPC),
            tooltip: getColumnTexts(TABLE_COLUMN_CPC, mode),
            cellComponent: DefaultCellRightAlign,
            headerComponent: DefaultCellHeaderRightAlign,
            format: "CPC",
            width: 86,
        },

        showPosition && {
            field: TABLE_COLUMN_POSITION,
            displayName: getColumnTexts(TABLE_COLUMN_POSITION, "title"),
            sortable: sortableFields.includes(TABLE_COLUMN_POSITION),
            cellComponent: (props) => {
                const row = props.row;
                row.CurrentPosition = row.Position;
                const params = {
                    ...props,
                    row: row,
                };
                return <MobileV2TableKeywordPositionCall params={params} />;
            },
            headerComponent: DefaultCellHeaderRightAlign,
            tooltip: getColumnTexts(TABLE_COLUMN_POSITION, mode),
            width: 98,
            showTooltip: true,
        },
        {
            field: TABLE_COLUMN_TOP_URL,
            displayName: getColumnTexts(TABLE_COLUMN_TOP_URL, "title"),
            cellComponent: UrlCellWebsiteAnalysis,
            headerComponent: DefaultCellHeader,
            cellClass: "url-cell",
            tooltip: getColumnTexts(TABLE_COLUMN_TOP_URL, mode),
            width: 232,
        },
        {
            field: TABLE_COLUMN_SERP_FEATURES,
            displayName: getColumnTexts(TABLE_COLUMN_SERP_FEATURES, "title"),
            cellComponent: (props) => {
                const { tableOptions, row } = props;
                tableOptions.sitesForSerpColumn = [{ name: row.Domain }];
                tableOptions.serpFeatureItemBackgroundColor = colorsPalettes.sky["200"];
                return (
                    <SerpTableCellSingle
                        {...props}
                        tableOptions={tableOptions}
                    />
                );
            },
            headerComponent: DefaultCellHeader,
            tooltip: getColumnTexts(TABLE_COLUMN_SERP_FEATURES, mode, entities[entity]),
            width: 140,
        },
    ]
        .filter((x) => x)
        .map((col) => {
            const isVisible = visibleColumns.includes(col.field) || col.visible;
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                ...col,
                isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : DEFAULT_SORT_DIRECTION,
                visible: isVisible,
            };
        });
};

export default getTableColumns;
